import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  CheckCheck,
  FileText,
  HardHat,
  MapPin,
  Send,
  Shield,
  User,
  Users,
  Loader2,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { useMyJobPacket, useAcknowledgeMyJobPacket } from '@/hooks/use-my-job-packets';
import { useOrgTimeZone } from '@/hooks/use-settings';
import type { WorkPackageSnapshot } from '@/lib/types';
import { isAxiosError } from 'axios';
import { toast } from '@/app/components/ui/simple-toaster';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import {
  PlanInitialsFields,
  type PlanInitialRevision,
} from '@/app/components/safety/plan-initials-fields';
import type { PlanInitialInput } from '@/lib/types';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  viewed: { label: 'Viewed', color: 'bg-amber-100 text-amber-800' },
  acknowledged: { label: 'Acknowledged', color: 'bg-green-100 text-green-800' },
};

function SafetyDocsSection({ docs }: { docs: WorkPackageSnapshot['safety_docs'] }) {
  if (docs.length === 0) return null;
  const typeLabels: Record<string, string> = {
    jsa: 'JSA',
    swp: 'Safe Work Plan',
    rescue_plan: 'Rescue Plan',
  };
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-orange-500" />
          Safety Documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {docs.map((doc) => (
            <Link
              key={`${doc.type}-${doc.id}`}
              to={doc.url}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-accent transition-colors"
            >
              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.title}</p>
                <p className="text-xs text-gray-500">
                  {typeLabels[doc.type] ?? doc.type}
                  {doc.version ? ` · Revision ${doc.version}` : ''}
                </p>
              </div>
              <Badge variant="outline" className="text-xs flex-shrink-0">{doc.status}</Badge>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function CrewSection({ crew }: { crew: WorkPackageSnapshot['crew'] }) {
  if (crew.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-500" />
          Crew
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {crew.map((member) => (
            <div key={member.tech_id} className="flex items-center gap-3 p-2 text-sm">
              <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-900">{member.name}</span>
              {member.role && (
                <Badge variant="outline" className="text-xs">{member.role}</Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function GearSection({ gear }: { gear: WorkPackageSnapshot['gear_checklist'] }) {
  if (gear.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HardHat className="h-4 w-4 text-purple-500" />
          Gear &amp; Rescue Checklist
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {gear.map((item) => (
            <div key={item.id} className="flex items-center gap-3 p-2 border rounded text-sm">
              <div className="flex-1 min-w-0">
                <p className="text-gray-900">{item.notes || `Gear Request #${item.id}`}</p>
                <p className="text-xs text-gray-500">Priority: {item.priority} &middot; Status: {item.status}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function MyJobPacketDetailPage() {
  const orgTimeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const packetId = Number(id);
  const { data: packet, isLoading, isError } = useMyJobPacket(packetId);
  const acknowledgeMutation = useAcknowledgeMyJobPacket();
  const [planInitials, setPlanInitials] = useState<PlanInitialInput[]>([]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (isError || !packet) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-16 text-gray-500">Job packet not found.</div>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[packet.status] ?? STATUS_CONFIG.sent;
  const wp = packet.work_package_snapshot;
  const initialPlans: PlanInitialRevision[] = (wp.safety_docs ?? [])
    .filter((doc) => doc.type === 'swp' || doc.type === 'rescue_plan')
    .map((doc): PlanInitialRevision => {
      const planType = doc.type === 'swp' ? 'safe_work_plan' : 'rescue_plan';
      const existing = packet.plan_initials.find(
        (item) =>
          item.plan_type === planType && item.plan_revision_id === doc.id,
      );
      return {
        plan_type: planType,
        id: doc.id,
        title: doc.title,
        version: doc.version ?? 1,
        url: doc.url,
        initialled_at: existing?.initialled_at,
        initials: existing?.initials,
      };
    })
    // Archived plans can't take new initials; keep them only as history.
    .filter(
      (plan) =>
        plan.initialled_at
        || !(packet.archived_plan_revisions ?? []).some(
          (item) =>
            item.plan_type === plan.plan_type
            && item.plan_revision_id === plan.id,
        ),
    );
  const hasPendingPlans = initialPlans.some((plan) => !plan.initialled_at);

  const handleAcknowledge = () => {
    acknowledgeMutation.mutate({ id: packetId, plan_initials: planInitials }, {
      onSuccess: () => {
        toast.success(
          packet.status === 'acknowledged'
            ? 'Plan initials recorded.'
            : 'Packet acknowledged — your supervisor has been notified.',
        );
        setPlanInitials([]);
      },
      onError: (error) => {
        const data = isAxiosError<{ plan_initials?: string | string[]; error?: string; detail?: string }>(error)
          ? error.response?.data
          : undefined;
        const reason = data?.plan_initials
          ? (Array.isArray(data.plan_initials) ? data.plan_initials.join(' ') : data.plan_initials)
          : data?.error ?? data?.detail;
        toast.error(
          reason
            ?? (packet.status === 'acknowledged'
              ? 'Failed to record plan initials.'
              : 'Failed to acknowledge packet.'),
        );
      },
    });
  };
  const hasPlanInitials = planInitials.some((item) => item.initials.trim());

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl mx-auto">
      {/* Back link */}
      <Link
        to="/my-dashboard"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to My Dashboard
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {packet.project_name || 'Job Packet'}
          </h1>
          {packet.project_number && (
            <p className="text-gray-500 mt-1">{packet.project_number}</p>
          )}
        </div>
        <Badge className={statusCfg.color + ' text-sm px-3 py-1'}>{statusCfg.label}</Badge>
      </div>

      {/* Assignment summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-500" />
            Assignment Details
          </CardTitle>
          <CardDescription>H3Kit field companion &mdash; your dispatch information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {packet.location && (
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span>{packet.location}</span>
            </div>
          )}
          {packet.role && (
            <div className="flex items-center gap-2 text-gray-700">
              <User className="h-4 w-4 text-gray-400" />
              <span>Role: {packet.role}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>Sent: {formatDateTimeInTimeZone(packet.sent_at, orgTimeZone)}</span>
          </div>
          {packet.viewed_at && (
            <div className="flex items-center gap-2 text-gray-700">
              <Eye className="h-4 w-4 text-gray-400" />
              <span>Viewed: {formatDateTimeInTimeZone(packet.viewed_at, orgTimeZone)}</span>
            </div>
          )}
          {packet.acknowledged_at && (
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCheck className="h-4 w-4 text-gray-400" />
              <span>
                Acknowledged: {formatDateTimeInTimeZone(packet.acknowledged_at, orgTimeZone)}
              </span>
            </div>
          )}
          {packet.notes_for_tech && (
            <div className="mt-3 p-3 bg-gray-50 dark:bg-accent rounded-lg">
              <p className="text-xs font-medium text-gray-500 mb-1">Notes from supervisor</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{packet.notes_for_tech}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Work-package snapshot sections */}
      {wp && (
        <>
          {wp.site && wp.site.address && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-red-500" />
                  Site
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700">
                <p className="font-medium">{wp.site.location_name}</p>
                <p>{wp.site.address}</p>
              </CardContent>
            </Card>
          )}

          {wp.client && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Client</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-1">
                <p className="font-medium">{wp.client.name}</p>
                {wp.client.contact_name && <p>Contact: {wp.client.contact_name}</p>}
                {wp.client.contact_phone && <p>Phone: {wp.client.contact_phone}</p>}
                {wp.client.contact_email && <p>Email: {wp.client.contact_email}</p>}
              </CardContent>
            </Card>
          )}

          <CrewSection crew={wp.crew ?? []} />
          <SafetyDocsSection docs={wp.safety_docs ?? []} />
          <GearSection gear={wp.gear_checklist ?? []} />

          {(wp.documents ?? []).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Project Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(wp.documents ?? []).map((doc) => (
                    <div key={doc.id} className="flex items-center gap-3 p-2 text-sm border rounded">
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-900">{doc.name}</span>
                      <Badge variant="outline" className="text-xs ml-auto">{doc.document_type}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {(packet.status !== 'acknowledged' || initialPlans.length > 0) && (
        <div className="sticky bottom-4 space-y-4 bg-white dark:bg-background border rounded-lg p-4 shadow-lg">
          {initialPlans.length > 0 && (
            <p className="text-sm text-muted-foreground">Open each linked plan revision before adding your initials.</p>
          )}
          {initialPlans.length > 0 && (
            <PlanInitialsFields
              plans={initialPlans}
              values={planInitials}
              onChange={setPlanInitials}
              disabled={acknowledgeMutation.isPending}
              timeZone={orgTimeZone}
            />
          )}
          {(packet.status !== 'acknowledged' || hasPendingPlans) && (
            <Button
              onClick={handleAcknowledge}
              disabled={
                acknowledgeMutation.isPending
                || (packet.status === 'acknowledged' && !hasPlanInitials)
              }
              size="lg"
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {acknowledgeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {packet.status === 'acknowledged' ? 'Recording...' : 'Acknowledging...'}
                </>
              ) : (
                <>
                  <CheckCheck className="h-4 w-4 mr-2" />
                  {packet.status === 'acknowledged'
                    ? 'Record Plan Initials'
                    : 'Acknowledge Job Packet'}
                </>
              )}
            </Button>
          )}
          <p className="text-xs text-center text-gray-500 mt-2">
            {packet.status === 'acknowledged'
              ? hasPendingPlans
                ? 'Return here after a later briefing to record missing initials.'
                : 'Current plan revisions are initialled.'
              : 'Your plan initials and packet acknowledgement are recorded separately.'}
          </p>
        </div>
      )}
    </div>
  );
}
